import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DEFAULT_GC_TIME } from '@/client/constants';
import { useClientStore } from '@/client/stores/client';
import { withErrorHandling } from '@/client/utils/errors';
import { AckInfo } from '@/client/types/scheduler';
import {
  AppDetailResponse,
  CloudAuditRow,
  CloudOverview,
  CloudWindow,
  DatabaseDetailResponse,
  Deployment,
  DeploymentsResponse,
  DnsRecordWrite,
  DomainResponse,
  DropletAction,
  DropletDetailResponse,
  EnvWrite,
  IssuesResponse,
  LogResponse,
  LogType,
  MetricWindow,
  ResourcesResponse,
  SpendResponse,
  TrendWindow,
  TrustedSourceType,
  TrendsResponse,
} from '@/client/types/cloud';

const BASE = '/api/admin/v2/cloud';

const qs = (params: Record<string, unknown>) => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    search.set(key, String(value));
  });
  const out = search.toString();
  return out ? `?${out}` : '';
};

export const cloudKeys = {
  all: ['cloud-v2'] as const,
  overview: (window: string) => [...cloudKeys.all, 'overview', window] as const,
  resources: () => [...cloudKeys.all, 'resources'] as const,
  trends: (window: string) => [...cloudKeys.all, 'trends', window] as const,
  spend: () => [...cloudKeys.all, 'spend'] as const,
  droplet: (id: string, window: string) => [...cloudKeys.all, 'droplet', id, window] as const,
  database: (id: string) => [...cloudKeys.all, 'database', id] as const,
  domain: (name: string) => [...cloudKeys.all, 'domain', name] as const,
  app: (id: string, window: string) => [...cloudKeys.all, 'app', id, window] as const,
  deployments: (window: string) => [...cloudKeys.all, 'deployments', window] as const,
  issues: (status: string) => [...cloudKeys.all, 'issues', status] as const,
  audit: (limit: number) => [...cloudKeys.all, 'audit', limit] as const,
  logs: (id: string, component: string, type: string, lines: number, deploymentId: string) =>
    [...cloudKeys.all, 'logs', id, component, type, lines, deploymentId] as const,
};

/** Admin v2 wraps payloads in `{ status, data }`; accept either shape. */
const unwrap = <T>(raw: unknown): T => {
  if (raw != null && typeof raw === 'object' && 'data' in raw && 'status' in raw) {
    return (raw as { data: T }).data;
  }
  return raw as T;
};

const useGet = () => {
  const request = useClientStore((s) => s.request);
  const fn = withErrorHandling(
    async (path: string): Promise<unknown> =>
      unwrap<unknown>(await request<unknown>(`${BASE}${path}`)),
    'Failed to load cloud resources',
  );
  // withErrorHandling erases generics; restore the typed call.
  return fn as unknown as <T>(path: string) => Promise<T>;
};

const id = encodeURIComponent;

// The server holds the estate for five minutes; asking more often buys nothing.
const READ = { staleTime: 1000 * 60, gcTime: DEFAULT_GC_TIME, placeholderData: keepPreviousData };

export const useCloudOverviewQuery = (window: CloudWindow, enabled = true) => {
  const get = useGet();
  return useQuery({
    queryKey: cloudKeys.overview(window),
    queryFn: () => get<CloudOverview>(`/overview${qs({ window })}`),
    enabled,
    ...READ,
  });
};

export const useCloudTrendsQuery = (window: TrendWindow, enabled = true) => {
  const get = useGet();
  return useQuery({
    queryKey: cloudKeys.trends(window),
    queryFn: () => get<TrendsResponse>(`/trends${qs({ window })}`),
    enabled,
    ...READ,
  });
};

export const useCloudSpendQuery = (enabled = true) => {
  const get = useGet();
  return useQuery({
    queryKey: cloudKeys.spend(),
    queryFn: () => get<SpendResponse>('/spend'),
    enabled,
    staleTime: 1000 * 60 * 30,
    gcTime: DEFAULT_GC_TIME,
  });
};

export const useCloudDropletQuery = (dropletId: string, window: MetricWindow, enabled = true) => {
  const get = useGet();
  return useQuery({
    queryKey: cloudKeys.droplet(dropletId, window),
    queryFn: () => get<DropletDetailResponse>(`/droplets/${id(dropletId)}${qs({ window })}`),
    enabled: enabled && !!dropletId,
    ...READ,
  });
};

export const useCloudDatabaseQuery = (databaseId: string, enabled = true) => {
  const get = useGet();
  return useQuery({
    queryKey: cloudKeys.database(databaseId),
    queryFn: () => get<DatabaseDetailResponse>(`/databases/${id(databaseId)}`),
    enabled: enabled && !!databaseId,
    ...READ,
  });
};

export const useCloudDomainQuery = (domain: string, enabled = true) => {
  const get = useGet();
  return useQuery({
    queryKey: cloudKeys.domain(domain),
    queryFn: () => get<DomainResponse>(`/domains/${id(domain)}/records`),
    enabled: enabled && !!domain,
    ...READ,
  });
};

/** The whole estate in one read; the page filters and groups it locally. */
export const useCloudResourcesQuery = (enabled = true) => {
  const get = useGet();
  return useQuery({
    queryKey: cloudKeys.resources(),
    queryFn: () => get<ResourcesResponse>('/resources'),
    enabled,
    ...READ,
  });
};

export const useCloudAppQuery = (appId: string, window: MetricWindow, enabled = true) => {
  const get = useGet();
  return useQuery({
    queryKey: cloudKeys.app(appId, window),
    queryFn: () => get<AppDetailResponse>(`/apps/${id(appId)}${qs({ window })}`),
    enabled: enabled && !!appId,
    // A deployment in flight changes by the minute.
    refetchInterval: (query) => (query.state.data?.app.in_progress_deployment ? 15_000 : false),
    ...READ,
  });
};

export const useCloudDeploymentsQuery = (window: CloudWindow, enabled = true) => {
  const get = useGet();
  return useQuery({
    queryKey: cloudKeys.deployments(window),
    queryFn: () => get<DeploymentsResponse>(`/deployments${qs({ window, limit: 300 })}`),
    enabled,
    ...READ,
  });
};

export const useCloudIssuesQuery = (status: 'open' | 'acknowledged' | 'all', enabled = true) => {
  const get = useGet();
  return useQuery({
    queryKey: cloudKeys.issues(status),
    queryFn: () => get<IssuesResponse>(`/issues${qs({ status })}`),
    enabled,
    ...READ,
  });
};

export const useCloudAuditQuery = (limit = 200, enabled = true) => {
  const get = useGet();
  return useQuery({
    queryKey: cloudKeys.audit(limit),
    queryFn: () => get<{ count: number; rows: CloudAuditRow[] }>(`/audit${qs({ limit })}`),
    enabled,
    ...READ,
  });
};

export const useCloudLogsQuery = (
  params: { appId: string; component: string; type: LogType; lines: number; deploymentId?: string },
  enabled = true,
) => {
  const get = useGet();
  const { appId, component, type, lines, deploymentId = '' } = params;
  return useQuery({
    queryKey: cloudKeys.logs(appId, component, type, lines, deploymentId),
    queryFn: () =>
      get<LogResponse>(
        `/apps/${id(appId)}/logs${qs({ component, type, lines, deployment_id: deploymentId })}`,
      ),
    enabled: enabled && !!appId && !!component,
    staleTime: 10_000,
    gcTime: DEFAULT_GC_TIME,
  });
};

/** Asks the server to rebuild its copy of the estate, then reloads every read. */
export const useCloudRefresh = () => {
  const get = useGet();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => get<ResourcesResponse>('/resources?fresh=1'),
    onSettled: () => queryClient.invalidateQueries({ queryKey: cloudKeys.all }),
  });
};

/** Writes: one helper, then a thin hook per action that invalidates every cloud read. */
const useWrite = <TVars, TRes>(
  build: (vars: TVars) => { path: string; method: 'POST' | 'PUT' | 'DELETE'; body: unknown },
  message: string,
) => {
  const request = useClientStore((s) => s.request);
  const queryClient = useQueryClient();
  const fn = withErrorHandling(async (vars: TVars): Promise<TRes> => {
    const { path, method, body } = build(vars);
    return unwrap<TRes>(await request<unknown>(`${BASE}${path}`, method, body));
  }, message);
  return useMutation({
    mutationFn: fn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: cloudKeys.all }),
  });
};

type AppResult = { app_id: string; deployment: Deployment | null };

export const useDeployAppMutation = () =>
  useWrite<{ appId: string; reason: string; forceBuild?: boolean }, AppResult>(
    (v) => ({
      path: `/apps/${id(v.appId)}/deploy`,
      method: 'POST',
      body: { reason: v.reason, force_build: !!v.forceBuild },
    }),
    'Failed to start the deployment',
  );

export const useRestartAppMutation = () =>
  useWrite<{ appId: string; reason: string; components?: string[] }, AppResult>(
    (v) => ({
      path: `/apps/${id(v.appId)}/restart`,
      method: 'POST',
      body: { reason: v.reason, components: v.components ?? [] },
    }),
    'Failed to restart the app',
  );

export const useCancelDeploymentMutation = () =>
  useWrite<{ appId: string; deploymentId: string; reason: string }, AppResult>(
    (v) => ({
      path: `/apps/${id(v.appId)}/deployments/${id(v.deploymentId)}/cancel`,
      method: 'POST',
      body: { reason: v.reason },
    }),
    'Failed to cancel the deployment',
  );

export const useRollbackAppMutation = () =>
  useWrite<{ appId: string; deploymentId: string; reason: string }, AppResult>(
    (v) => ({
      path: `/apps/${id(v.appId)}/rollback`,
      method: 'POST',
      body: { reason: v.reason, deployment_id: v.deploymentId },
    }),
    'Failed to roll back',
  );

export const useScaleComponentMutation = () =>
  useWrite<
    { appId: string; component: string; count: number; reason: string },
    { app_id: string; component: string; from: number; to: number }
  >(
    (v) => ({
      path: `/apps/${id(v.appId)}/components/${id(v.component)}/scale`,
      method: 'PUT',
      body: { reason: v.reason, instance_count: v.count },
    }),
    'Failed to scale the component',
  );

/** Power cycle and shut down take cloud.manage and their own route; the rest take cloud.operate. */
export const useDropletActionMutation = () =>
  useWrite<
    { dropletId: string; action: DropletAction; reason: string },
    { droplet_id: string; action: { id: number; status: string; type: string } }
  >(
    (v) => ({
      path: `/droplets/${id(v.dropletId)}/${
        v.action === 'power_cycle' || v.action === 'shutdown' ? 'power' : 'actions'
      }`,
      method: 'POST',
      body: { action: v.action, reason: v.reason },
    }),
    'Failed to act on the Droplet',
  );

/** Saving starts a deployment. Values go one way: they are never read back. */
export const useSetEnvMutation = () =>
  useWrite<
    { appId: string; target: string; set: EnvWrite[]; unset: string[]; reason: string },
    { app_id: string; added: string[]; replaced: string[]; removed: string[] }
  >(
    (v) => ({
      path: `/apps/${id(v.appId)}/env`,
      method: 'PUT',
      body: { target: v.target, set: v.set, unset: v.unset, reason: v.reason },
    }),
    'Failed to change the variables',
  );

export const useResizeDatabaseMutation = () =>
  useWrite<{ databaseId: string; size: string; reason: string }, { from: string; to: string }>(
    (v) => ({
      path: `/databases/${id(v.databaseId)}/size`,
      method: 'PUT',
      body: { size: v.size, reason: v.reason },
    }),
    'Failed to resize the database',
  );

/** `confirmed` accepts the consequence the server would otherwise answer 409 with. */
export const useTrustSourceMutation = () =>
  useWrite<
    {
      databaseId: string;
      type: TrustedSourceType;
      value: string;
      reason: string;
      confirmed: boolean;
    },
    { trusted_sources: number }
  >(
    (v) => ({
      path: `/databases/${id(v.databaseId)}/trusted-sources`,
      method: 'POST',
      body: { type: v.type, value: v.value, reason: v.reason, confirmed: v.confirmed },
    }),
    'Failed to add the trusted source',
  );

export const useUntrustSourceMutation = () =>
  useWrite<
    { databaseId: string; uuid: string; reason: string; confirmed: boolean },
    { trusted_sources: number }
  >(
    (v) => ({
      // A proxy may drop the body of a DELETE; the server reads these from the query too.
      path: `/databases/${id(v.databaseId)}/trusted-sources/${id(v.uuid)}${qs({
        reason: v.reason,
        confirmed: v.confirmed ? 1 : undefined,
      })}`,
      method: 'DELETE',
      body: { reason: v.reason, confirmed: v.confirmed },
    }),
    'Failed to remove the trusted source',
  );

/** Creates when there is no `recordId`, otherwise changes that record. */
export const useSaveRecordMutation = () =>
  useWrite<
    { domain: string; recordId?: number; record: DnsRecordWrite; reason: string },
    { domain: string }
  >(
    (v) => ({
      path: `/domains/${id(v.domain)}/records${v.recordId ? `/${v.recordId}` : ''}`,
      method: v.recordId ? 'PUT' : 'POST',
      body: { ...v.record, reason: v.reason },
    }),
    'Failed to save the record',
  );

export const useDeleteRecordMutation = () =>
  useWrite<{ domain: string; recordId: number; reason: string }, { deleted: string }>(
    (v) => ({
      path: `/domains/${id(v.domain)}/records/${v.recordId}${qs({ reason: v.reason })}`,
      method: 'DELETE',
      body: { reason: v.reason },
    }),
    'Failed to delete the record',
  );

export const useAckIssueMutation = () =>
  useWrite<{ issueId: string; note?: string; resourceName?: string }, { id: string; ack: AckInfo }>(
    (v) => ({
      path: '/issues/ack',
      method: 'POST',
      body: { id: v.issueId, note: v.note, resource_name: v.resourceName },
    }),
    'Failed to acknowledge the issue',
  );
